import {
  Container,
  Header,
  SpaceBetween,
  ColumnLayout,
  Input,
  FormField,
  TextContent,
  Box,
  Tabs,
  TabsProps,
  Select,
} from "@cloudscape-design/components-themed";
import { isEqual, isEmpty } from "lodash";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  ConfirmationMerchantInfo,
  ConfirmationMerchantInfoItem,
  ConfirmResult,
  CSDict,
} from "../../../features/api-interfaces";
import { useCodelistHook } from "../../../app/useCodelistHook";
import { getMessage } from "../../../features/error/messages";
import { getIHRLabel2 } from "../../../utils/common";
import { isLengthValid } from "../../../utils/inputValidation";
import {
  COMMENT_MAX_LENGTH,
  LABELS,
  REQUIRED_REPRESENTATIVE_CONFIRMATION,
  CORPORATE_NUMBER_MISMATCH_HAS_EXISTING,
  CORPORATE_NUMBER_MISMATCH_NO_EXISTING,
  CHANGE_APPLICATION_CONFIRMATION,
  REQUIRES_ADDRESS_CONFIRMATION,
  DEFAULT_COMMENT,
  INDIVIDUAL_COUNSELING_PROJECT,
} from "../constants";
import { getIndividualHandlingReason, getRateCategoryCodeDict, getTargetRateItems } from "../util";
import ChangeApplicationConfirmation from "./ChangeApplicationConfirmation";
import CompanyAddressConfirmation from "./CompanyAddressConfirmation";
import CorporateNumberMismatch from "./CorporateNumberMismatch";
import IndividualCounselingProject from "./IndividualCounselingProject";
import RepresentativeConfirmation from "./RepresentativeConfirmation";
import { addBlank } from "../../default-constants";
import { useGetIndividualHandlingConfirmationTargetRateQuery } from "../../../features/api";
import { UPDATE_DIFFERENCIAL } from "../../../configs/constants";

interface IMerchant {
  merchant: ConfirmationMerchantInfo;
  setMerchant: Dispatch<SetStateAction<ConfirmationMerchantInfo | undefined>>;
  isMerchantRegistered: boolean;
  confirmResultList: ConfirmResult[];
}

/**
 * 個別取扱確認更新 加盟店
 */
const Merchant = (props: IMerchant) => {
  const { merchant, setMerchant, isMerchantRegistered, confirmResultList } =
    props;
  const { individualHandlingReason } = useCodelistHook();
    const { data: targetRates } =
    useGetIndividualHandlingConfirmationTargetRateQuery();
  // 個別相談案件関連
  const individualCounselingProjectList = merchant.merchantInfoList.filter(
    (info) =>
      INDIVIDUAL_COUNSELING_PROJECT.find((item) =>
        isEqual(info.individualHandlingReason, item)
      )
  );
  // 要代表者確認
  const representativeConfirmation = getIndividualHandlingReason(
    REQUIRED_REPRESENTATIVE_CONFIRMATION,
    merchant.merchantInfoList
  );
  // 法人番号相違（既存あり）
  const corporateNumberMismatchHasExisting = getIndividualHandlingReason(
    CORPORATE_NUMBER_MISMATCH_HAS_EXISTING,
    merchant.merchantInfoList
  );
  // 法人番号相違（既存なし）
  const corporateNumberMismatchNoExisting = getIndividualHandlingReason(
    CORPORATE_NUMBER_MISMATCH_NO_EXISTING,
    merchant.merchantInfoList
  );
  // 要変更申請確認
  const changeApplicationConfirmation = getIndividualHandlingReason(
    CHANGE_APPLICATION_CONFIRMATION,
    merchant.merchantInfoList
  );
  // 事業者住所確認
  const companyAddressConfirmation = getIndividualHandlingReason(
    REQUIRES_ADDRESS_CONFIRMATION,
    merchant.merchantInfoList
  );
  const isNewAdd = isEqual(UPDATE_DIFFERENCIAL.NEW_ADD, merchant.updateDifferencial);
    const [commentErrorMsg, setCommentErrorMsg] = useState("");
  const [rateCategoryItems, setRateCategoryItems] = useState<CSDict[]>([]);
  

    useEffect(() => {
    setRateCategoryItems(addBlank(getTargetRateItems(targetRates)));
  }, [targetRates]);

  /**
   * コメントの入力ハンドラ
   * @param {string} inputValue
   */
  const commentOnChange = (inputValue: string) => {
    setMerchant({
      ...merchant,
      comment: inputValue,
    });
    const errorMsg = isLengthValid(inputValue, COMMENT_MAX_LENGTH)
      ? ""
      : getMessage("KGRERR058", COMMENT_MAX_LENGTH).label;
    setCommentErrorMsg(errorMsg);
  };

  /**
   * タブの生成
   */
  const getTabs = () => {
    const tabs: TabsProps.Tab[] = [];

    if (individualCounselingProjectList?.length > 0) {
      tabs.push({
        label: getIHRLabel2(
          individualHandlingReason,
          individualCounselingProjectList
        ),
        id: "individualCounselingProject",
        content: (
          <IndividualCounselingProject
            merchantList={individualCounselingProjectList}
            updateItemList={updateItemList}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }
    if (representativeConfirmation) {
      tabs.push({
        label: "要代表者確認",
        id: "representativeConfirmation",
        content: (
          <RepresentativeConfirmation
            merchant={representativeConfirmation}
            updateItem={updateItem}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }
    if (corporateNumberMismatchHasExisting) {
      tabs.push({
        label: "法人番号相違（既存あり）",
        id: "corporateNumberMismatchHasExisting",
        content: (
          <CorporateNumberMismatch
            merchant={corporateNumberMismatchHasExisting}
            updateItem={updateItem}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }
    if (corporateNumberMismatchNoExisting) {
      tabs.push({
        label: "法人番号相違（既存なし）",
        id: "corporateNumberMismatch",
        content: (
          <CorporateNumberMismatch
            merchant={corporateNumberMismatchNoExisting}
            updateItem={updateItem}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }

    if (changeApplicationConfirmation) {
      tabs.push({
        label: "変更申請確認",
        id: "changeApplicationConfirmation",
        content: (
          <ChangeApplicationConfirmation
            merchant={changeApplicationConfirmation}
            updateItem={updateItem}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }

    if (companyAddressConfirmation) {
      tabs.push({
        label: "事業者住所確認",
        id: "companyAddressConfirmation",
        content: (
          <CompanyAddressConfirmation
            merchant={companyAddressConfirmation}
            updateItem={updateItem}
            isMerchantRegistered={isMerchantRegistered}
            confirmResultList={confirmResultList}
          />
        ),
      });
    }

    return tabs;
  };

  /**
   * データ更新ハンドラ
   * @param {ConfirmationMerchantInfoItem} merchantInfo
   */
  const updateItemList = (merchantInfoList: ConfirmationMerchantInfoItem[]) => {
    const updatedList = merchant.merchantInfoList.map(
      (item) =>
        merchantInfoList.find((target) =>
          isEqual(
            item.individualHandlingReason,
            target.individualHandlingReason
          )
        ) ?? item
    );

    setMerchant({
      ...merchant,
      comment:
        isEqual(merchantInfoList[0].confirmationResult, false) &&
        isEmpty(merchant.comment)
          ? DEFAULT_COMMENT
          : merchant.comment ?? "",
      merchantInfoList: updatedList,
    });
  };

  /**
   * データ更新ハンドラ
   * @param {ConfirmationMerchantInfoItem} merchantInfo
   */
  const updateItem = (merchantInfo: ConfirmationMerchantInfoItem) => {
    const updatedList = merchant.merchantInfoList.map((item) =>
      isEqual(
        item.individualHandlingReason,
        merchantInfo.individualHandlingReason
      )
        ? { ...merchantInfo }
        : item
    );

    setMerchant({
      ...merchant,
      comment:
        isEqual(merchantInfo.confirmationResult, false) &&
        isEmpty(merchant.comment)
          ? DEFAULT_COMMENT
          : merchant.comment ?? "",
      merchantInfoList: updatedList,
    });
  };

  /**
   * 料率カテゴリ（連携予定）の入力ハンドラ
   * @param {string | undefined}} value 選択された料率カテゴリのvalue
   */
  const rateCategoryOnchange = (value: string | undefined) => {
    setMerchant({
      ...merchant,
      plannedRateCategoryCode: isEmpty(value) ? null : Number(value),
    });
  }

  return (
    <Container header={<Header>{LABELS.MERCHANT}</Header>}>
      <SpaceBetween direction={"vertical"} size={"s"}>
        <Box>
          <Box margin={"s"} float={"left"}>
            <TextContent>
              <h5>{LABELS.INDIVIDUAL_HANDLING_REASON}</h5>
            </TextContent>
          </Box>
          <ColumnLayout columns={2}>
            <FormField>
              <Input
                value={getIHRLabel2(
                  individualHandlingReason,
                  merchant.merchantInfoList
                )}
                readOnly
              />
            </FormField>
          </ColumnLayout>
        </Box>
        <SpaceBetween direction={"horizontal"} size={"s"}>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.COMMENT}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={0}>
              <FormField
                constraintText={
                  isMerchantRegistered
                    ? LABELS.BLANK_LONG
                    : `${COMMENT_MAX_LENGTH}文字以内${LABELS.BLANK_SUPER_LONG}`
                }
                errorText={commentErrorMsg}
              >
                <Input
                  value={merchant.comment ?? ""}
                  onChange={({ detail }) => {
                    commentOnChange(detail.value);
                  }}
                  disabled={isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
        </SpaceBetween>
        <SpaceBetween direction={"horizontal"} size={"s"}>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.RATE_CATEGORY_PLANNED}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <FormField constraintText={LABELS.BLANK_RATE_CATEGORY}>
                <Select
                  selectedOption={getRateCategoryCodeDict(
                    rateCategoryItems,
                    merchant.plannedRateCategoryCode
                  )}
                  options={rateCategoryItems}
                  filteringType={"none"}
                  onChange={({ detail }) => rateCategoryOnchange(detail.selectedOption.value)}
                  disabled={isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
          {!isNewAdd && (
            <Box>
              <Box margin={"s"} float={"left"}>
                <TextContent>
                  <h5>{LABELS.RATE_CATEGORY_LINKED}</h5>
                </TextContent>
              </Box>
              <ColumnLayout columns={1}>
                <FormField constraintText={LABELS.BLANK_RATE_CATEGORY}>
                  <Select
                    selectedOption={getRateCategoryCodeDict(
                      rateCategoryItems,
                      merchant.linkedRateCategoryCode
                    )}
                    options={rateCategoryItems}
                    filteringType={"none"}
                    disabled={true}
                  />
                </FormField>
              </ColumnLayout>
            </Box>
          )}
        </SpaceBetween>
        <Tabs tabs={getTabs()} />
      </SpaceBetween>
    </Container>
  );
};

export default Merchant;
